import type {
  IClaimInviteRequest,
  IClaimInviteResponse,
  IDeleteInvitationRequest,
  IDeleteInvitationResponse,
  IFlagWrongCompanyRequest,
  IFlagWrongCompanyResponse,
  IGetCurrentUserResponse,
  IGetUserByIdRequest,
  IGetUserByIdResponse,
  IGetUsersByRoleRequest,
  IGetUsersByRoleResponse,
  IInviteDetailsRequest,
  IInviteDetailsResponse,
  IInviteUserRequest,
  IInviteUserResponse,
  IResendInvitationRequest,
  IResendInvitationResponse,
  IUpdateOnboardingStatusRequest,
  IUpdateOnboardingStatusResponse,
  IUpdatePasswordRequest,
  IUpdatePasswordResponse,
  IUpdateSuperAdminDetailsRequest,
  IUpdateSuperAdminDetailsResponse,
  IUpdateSuperAdminPictureRequest,
  IUpdateSuperAdminPictureResponse,
  IUpdateUserDetailsRequest,
  IGetRequiresReconsentResponse,
  IPostPrivacyConsentResponse,
  IUpdateUserDetailsResponse,
  IUpdateUserPictureRequest,
  IUpdateUserPictureResponse,
} from "./types";
import type { R } from "../types";
import { axiosInstance } from "../../axios-instance";
import qs from "qs";
import { qsOptions } from "../qsOptions";
import type { AxiosRequestConfig } from "axios";

export class UsersService {
  async getCurrentUser(c?: AxiosRequestConfig): R<IGetCurrentUserResponse> {
    try {
      return axiosInstance.get(`/api/auth/currentUser`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async inviteUser(
    p: IInviteUserRequest,
    c?: AxiosRequestConfig,
  ): R<IInviteUserResponse> {
    try {
      const params = qs.stringify(p.params, qsOptions);
      return axiosInstance.post(`/api/admin/inviteUser?${params}`, p.body, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async resendInvitation(
    p: IResendInvitationRequest,
    c?: AxiosRequestConfig,
  ): R<IResendInvitationResponse> {
    try {
      return axiosInstance.post(`/api/admin/resendInvite`, p, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async deleteInvitation(
    p: IDeleteInvitationRequest,
    c?: AxiosRequestConfig,
  ): R<IDeleteInvitationResponse> {
    try {
      const params = qs.stringify(p, qsOptions);
      return axiosInstance.delete(`/api/admin/deleteInvite?${params}`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getUsersByRole(
    p: IGetUsersByRoleRequest,
    c?: AxiosRequestConfig,
  ): R<IGetUsersByRoleResponse> {
    try {
      const params = qs.stringify(p, qsOptions);
      return axiosInstance.get(`/api/user/allByRole?${params}`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getInviteDetailsByToken(
    p: IInviteDetailsRequest,
    c?: AxiosRequestConfig,
  ): R<IInviteDetailsResponse> {
    try {
      const params = qs.stringify(p, qsOptions);
      return axiosInstance.get(`/api/user/verifyInvitationLink?${params}`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async claimInvite(
    p: IClaimInviteRequest,
    c?: AxiosRequestConfig,
  ): R<IClaimInviteResponse> {
    try {
      const { token, ...body } = p;
      const params = qs.stringify({ token }, qsOptions);
      return axiosInstance.post(`/api/user/register?${params}`, body, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateUserDetails(
    p: IUpdateUserDetailsRequest,
    c?: AxiosRequestConfig,
  ): R<IUpdateUserDetailsResponse> {
    try {
      return axiosInstance.put(`/api/user/updateUserDetails`, p, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateOnboardingStatus(
    p: IUpdateOnboardingStatusRequest,
    c?: AxiosRequestConfig,
  ): R<IUpdateOnboardingStatusResponse> {
    try {
      const params = qs.stringify(p, qsOptions);
      return axiosInstance.put(`/api/user/onboardingStatus?${params}`, {}, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updatePassword(
    p: IUpdatePasswordRequest,
    c?: AxiosRequestConfig,
  ): R<IUpdatePasswordResponse> {
    try {
      return axiosInstance.put(`/api/user/changePassword`, p, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateProfilePicture(
    p: IUpdateUserPictureRequest,
    c?: AxiosRequestConfig,
  ): R<IUpdateUserPictureResponse> {
    try {
      const params = qs.stringify({ userId: p.userId }, qsOptions);
      return axiosInstance.put(
        `/api/user/updateProfilePicture?${params}`,
        p.picture,
        {
          ...c,
          headers: {
            ...c?.headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async flagWrongCompany(
    p: IFlagWrongCompanyRequest,
    c?: AxiosRequestConfig,
  ): R<IFlagWrongCompanyResponse> {
    try {
      const params = qs.stringify(p, qsOptions);
      return axiosInstance.put(
        `/api/user/flagWrongCompanyMapping?${params}`,
        {},
        c,
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getUserDetails(
    p: IGetUserByIdRequest,
    c?: AxiosRequestConfig,
  ): R<IGetUserByIdResponse> {
    try {
      return axiosInstance.get(`/api/user/${p.id}`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateSuperAdminDetails(
    p: IUpdateSuperAdminDetailsRequest,
    c?: AxiosRequestConfig,
  ): R<IUpdateSuperAdminDetailsResponse> {
    try {
      return axiosInstance.put(`/api/superadmin/updateUserDetails`, p, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getRequiresReconsent(
    c?: AxiosRequestConfig,
  ): R<IGetRequiresReconsentResponse> {
    try {
      return axiosInstance.get(`/api/user/privacy/requiresReconsent`, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async postPrivacyConsent(
    c?: AxiosRequestConfig,
  ): R<IPostPrivacyConsentResponse> {
    try {
      return axiosInstance.post(`/api/user/privacy/consent`, {}, c);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateSuperAdminProfilePicture(
    p: IUpdateSuperAdminPictureRequest,
    c?: AxiosRequestConfig,
  ): R<IUpdateSuperAdminPictureResponse> {
    try {
      const params = qs.stringify({ userId: p.userId }, qsOptions);
      return axiosInstance.put(
        `/api/superadmin/updateProfilePicture?${params}`,
        p.picture,
        {
          ...c,
          headers: {
            ...c?.headers,
            "Content-Type": "multipart/form-data",
          },
        },
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
